/**
 * Vespera ruft an — die Karte der SEITE, wenn ein Fokus den Anruf filtert.
 * (06.10.2026; die Prüfung zu pre.3646 nannte es „Befund 5“)
 *
 * ## Woher das kommt
 *
 * 06.10.2026, 11:50 (zweites Mal nach 07:27): Martin ließ Vespera sich selbst
 * anrufen. Serverseitig lief alles richtig — VoIP-Push zugestellt, dann
 * „[anruf-nativ] callkit lehnt ab 6db57b98 code=3“ (app=vorne), sofort die
 * Rückfall-Meldung „Vespera ruft an – Tippen zum Abheben“ (zugestellt 1/1),
 * 75 s später „verpasst“. Gesehen hat er NICHTS:
 *   - code=3 = CXErrorCodeIncomingCallError.filteredByDoNotDisturb: ein Fokus
 *     filtert den Anrufer „Vespera“ (CXHandle generic, kein erlaubbarer Kontakt).
 *   - Die Meldung ist im Fokus ohne die Berechtigung „Time Sensitive“ leise
 *     (fehlt in App.entitlements), und bei vorderer App zeigt iOS sie ohnehin
 *     nur, wenn die App es zulässt.
 *
 * ## Was diese Datei tut
 *
 * Kommt die Spur „callkit lehnt ab <8 Hex> code=3“ an der Seite vorbei (app.ts,
 * verbindeAnrufSpur — dieselbe Zeile, die auch ans Server-Protokoll geht) und
 * ist die Seite VORNE, zeigt sie selbst eine Karte „Vespera ruft an“ mit
 * „Abheben“ und „Ablehnen“ (die Ansicht: anruf-karte-ansicht.ts).
 *
 *   - Die Spur trägt nur 8 Hex-Zeichen. Die ganze callId holt die Seite mit
 *     ihrer Sitzung vom Server (GET /api/anruf/klingelt, nur eigene Aufträge),
 *     jede Abfrage mit Zeitlimit (8 s).
 *   - „Abheben“ geht genau den Weg des Tipps auf die Meldung: der Link
 *     `pawbot://anruf?callId=…` an behandleAnrufLink (lib/anruf-link.ts), dann
 *     /api/token mit der callId — der Server nimmt sie dort einmalig an
 *     (nimmAn verlangt „klingelt“ und eine noch gültige Frist).
 *   - „Ablehnen“ nimmt nur die Karte weg — wie Ablehnen am CallKit-Bildschirm
 *     (die App sagt dem Server dort auch nichts): der Auftrag wird nach der
 *     Frist „verpasst“, Glocke und Telegram melden es wie bisher.
 *   - Weg ist sie außerdem nach der Klingelfrist (`restMs` vom Server, nie
 *     gegen die Geräteuhr gerechnet), sobald der Server „klingelt nicht mehr“
 *     sagt (verpasst, angenommen, vorbei — Abfrage alle 5 s), sobald ein
 *     Gespräch läuft und wenn derselbe Anruf über die Meldung angenommen wird.
 *   - Einmal je Anruf-Kennung — aber erst, wenn ENTSCHIEDEN ist (Prüfbefund
 *     06.10.): Karte gezeigt, oder endgültig nein (404, falsche Form, anderes
 *     Konto, Seite hinten, schon im Gespräch …). Ist die Antwort nur VORLÄUFIG
 *     (Netz, Zeitlimit, 429, 5xx, Konto direkt nach dem Neuladen noch
 *     unbekannt), versucht sie es alle 5 s wieder, längstens die Klingelfrist
 *     (75 s). Eine doppelte Spur-Zeile startet keinen zweiten Versuch.
 *   - Ein kurzer Hinweis: einmal Haptik. Den Ton „achtung“ NUR im Browser und
 *     nur, wenn nichts aufgenommen wird — siehe anrufKarteTonErlaubt.
 *
 * ## Was sie NICHT ändert
 *
 * ⚠️ Ohne code=3 nichts: CallKit klingelt wie bisher, jede andere Ablehnung
 *    (2 schon gemeldet, 4 Sperrliste, 1 Berechtigung …) bleibt still.
 * ⚠️ Ist die Seite NICHT vorne (App im Hintergrund, gesperrt), bleibt es bei
 *    der Rückfall-Meldung des Servers — ohne „Time Sensitive“ im nativen Build
 *    ist sie im Fokus leise. Das löst nur ein neuer App-Build.
 */
import { fokusAblehnungAus } from "../../shared/anruf-callkit-spur.js";
import { anrufIdAusLink, anrufLink } from "./anruf-link.js";

/** So oft fragt die offene Karte, ob es noch klingelt (= Takt der Wache im Server). */
export const ANRUF_KARTE_PRUEF_MS = 5_000;
/** So oft sieht die offene Karte nach, ob inzwischen ein Gespräch läuft. */
export const ANRUF_KARTE_TAKT_MS = 1_000;
/**
 * Bleibt weniger Frist, zeigt sie sich nicht erst: bis /api/token ankommt
 * (Wechsel zum Chat, Mikrofon frei, Abruf), wäre der Auftrag abgelaufen — ein
 * Abheben, das sicher scheitert, wäre schlimmer als keine Karte.
 */
export const ANRUF_KARTE_MIN_REST_MS = 3_000;
/** Zeitlimit je Abfrage beim Server — eine hängende Antwort darf nichts sperren. */
export const ANRUF_KARTE_ABFRAGE_MS = 8_000;
/** Abstand der Versuche, solange die Antwort nur vorläufig ist. */
export const ANRUF_KARTE_NOCHMAL_MS = 5_000;
/**
 * So lange nach der Spur-Zeile wird es versucht: die Klingelfrist des Servers
 * (KLINGEL_FRIST_MS, 75 s). Danach klingelt es dort sicher nicht mehr.
 */
export const ANRUF_KARTE_VERSUCH_MS = 75_000;

export interface KlingelnderAnruf {
  callId: string;
  /** Rest der Klingelfrist laut Server (ms). */
  restMs: number;
}

/**
 * Die Abfrage ging schief. `vorlaeufig` = später nochmal fragen lohnt (Netz,
 * Zeitlimit, 429, 5xx); sonst ist die Antwort endgültig (401/403/400, falsche
 * Form) und ein zweiter Versuch brächte dasselbe.
 */
export class KlingeltAbfrageFehler extends Error {
  readonly vorlaeufig: boolean;
  readonly grund: string;
  constructor(vorlaeufig: boolean, grund: string) {
    super(`anruf-klingelt ${grund}`);
    this.vorlaeufig = vorlaeufig;
    this.grund = grund;
  }
}

/**
 * GET /api/anruf/klingelt — der klingelnde Anruf dieses Kontos zum Präfix.
 * `null` = es klingelt nicht (mehr) (404). Sonst wirft sie einen
 * KlingeltAbfrageFehler — der Aufrufer unterscheidet „vorbei“ von „gerade
 * nicht erreichbar“.
 *
 * ⚠️ Zeitlimit: nach `zeitlimitMs` wird abgebrochen (AbortController) UND die
 *    Abfrage gilt als gescheitert, auch wenn der Abruf das Signal überhört.
 */
export async function klingelndenAbfragen(
  praefix: string,
  abruf: typeof fetch = fetch,
  zeitlimitMs: number = ANRUF_KARTE_ABFRAGE_MS,
): Promise<KlingelnderAnruf | null> {
  const ac = new AbortController();
  const abgelaufen = new Promise<never>((_, nein) => {
    ac.signal.addEventListener(
      "abort",
      () => nein(new KlingeltAbfrageFehler(true, "zeitlimit")),
      { once: true },
    );
  });
  const uhr = setTimeout(() => ac.abort(), zeitlimitMs);
  try {
    let r: Response;
    try {
      r = await Promise.race([
        abruf(`/api/anruf/klingelt?praefix=${encodeURIComponent(praefix)}`, {
          credentials: "include",
          cache: "no-store",
          signal: ac.signal,
        }),
        abgelaufen,
      ]);
    } catch (e) {
      throw e instanceof KlingeltAbfrageFehler ? e : new KlingeltAbfrageFehler(true, "netz");
    }
    if (r.status === 404) return null;
    if (!r.ok) {
      throw new KlingeltAbfrageFehler(r.status === 429 || r.status >= 500, String(r.status));
    }
    let d: { callId?: unknown; restMs?: unknown } | null;
    try {
      d = (await Promise.race([r.json(), abgelaufen])) as typeof d;
    } catch (e) {
      throw e instanceof KlingeltAbfrageFehler ? e : new KlingeltAbfrageFehler(false, "form");
    }
    const callId = typeof d?.callId === "string" ? d.callId : "";
    const restMs = typeof d?.restMs === "number" ? d.restMs : Number.NaN;
    // Dieselbe Form, die der Link verlangt — und es muss DIESER Anruf sein.
    if (anrufIdAusLink(anrufLink(callId)) !== callId || !callId.startsWith(praefix)) {
      throw new KlingeltAbfrageFehler(false, "form");
    }
    if (!Number.isFinite(restMs) || restMs < 0) throw new KlingeltAbfrageFehler(false, "rest");
    return { callId, restMs };
  } finally {
    clearTimeout(uhr);
  }
}

/**
 * Darf der Hinweiston klingen?
 *
 * ⚠️ IN DER APP NIE (Prüfbefund 06.10.): der Ton läuft über den Kontext der
 *    kurzen Töne (audio-feedback.ts, playBeep), und ein laufender Kontext
 *    nimmt dem Mikrofon-Motor der App die Tonsitzung — am 02.10. war das
 *    Wach-Wort darum taub (tts-pcm.ts, „DER STILLE KONTEXT NAHM DEM WACH-WORT
 *    DAS MIKROFON“; Morse legt seinen Kontext deshalb nach dem Ton schlafen).
 *    Genau jetzt gleicht der Motor nativ ab (PawbotAnruf.swift, aufraeumen
 *    „callkit-abgelehnt“). In der App bleibt es bei der Haptik. Das Wach-Wort
 *    lauscht nur in der App — damit klingt der Ton auch nie, während es lauscht.
 * ⚠️ Im Browser nicht in eine laufende Aufnahme hinein (er landete in ihr) —
 *    dieselbe Regel wie die Quittung „gesendet“ (quittungFuerVersand).
 */
export function anrufKarteTonErlaubt(z: {
  nativ: boolean;
  nimmtAuf: boolean;
  tagesaufnahme: boolean;
}): boolean {
  return !z.nativ && !z.nimmtAuf && !z.tagesaufnahme;
}

/** Warum die Karte gegangen ist — steht in der Protokollzeile. */
type AnrufKarteEnde =
  | "abgehoben"
  | "abgelehnt"
  | "abgelaufen"
  | "vorbei"
  | "gespraech"
  | "anderswo";

export interface AnrufKarteHandlungen {
  abheben: () => void;
  ablehnen: () => void;
}

export interface AnrufKarteUmgebung {
  /** Nur das Hauptkonto wird angerufen (wie der Anruf-Knopf). */
  darfAnrufen: () => boolean;
  /**
   * Ist das Konto schon geladen? Direkt nach dem Neuladen nicht — dann ist
   * `darfAnrufen` noch falsch, und es wird später nochmal versucht. Fehlt = ja.
   */
  kontoBekannt?: () => boolean;
  /** Ist die Seite vorne? (`document.visibilityState === "visible"`) */
  sichtbar: () => boolean;
  /** Läuft schon ein Gespräch oder verbindet es gerade? */
  imGespraech: () => boolean;
  /** Wurde diese Kennung schon über die Meldung angenommen? (behandeltSpeicher) */
  schonBehandelt?: (callId: string) => boolean;
  /** Vorgabe: klingelndenAbfragen (der Server). */
  holeKlingelnden?: (praefix: string) => Promise<KlingelnderAnruf | null>;
  /** Die Karte zeigen (anruf-karte-ansicht.ts); `schliesse` nimmt sie weg. */
  zeige: (h: AnrufKarteHandlungen) => { schliesse: () => void };
  /** Abheben über den Weg der Meldung; das Ergebnis geht ins Protokoll. */
  abheben: (callId: string) => string | void;
  tonErlaubt: () => boolean;
  ton: () => void;
  haptik: () => void;
  /** Eine Zeile ins Server-Protokoll (meldeDiagnose). */
  spur: (zeile: string) => void;
}

export interface AnrufKarte {
  /**
   * Jede Zeile der nativen Spur kommt hier vorbei; nur code=3 zählt. Erfüllt,
   * wenn entschieden ist (bei vorläufigen Antworten erst nach den Versuchen).
   */
  spur: (zeile: string) => Promise<void>;
  /** Derselbe Anruf kam über die Meldung (Deep-Link): die Karte geht. */
  anderswo: (callId: string | null) => void;
  /** Die callId der offenen Karte, sonst null. */
  offen: () => string | null;
}

interface Offen {
  praefix: string;
  callId: string;
  ansicht: { schliesse: () => void };
  ablauf: ReturnType<typeof setTimeout>;
  takt: ReturnType<typeof setInterval>;
  /** Läuft für DIESE Karte gerade eine Nachfrage? (je Karte — eine hängende sperrt keine andere) */
  pruefungLaeuft: boolean;
}

/** Ergebnis eines Versuchs: endgültig (mit Grund, oder `null` = Karte gezeigt) oder vorläufig. */
type Versuch = { endgueltig: true; grund: string | null } | { endgueltig: false; grund: string };

const warte = (ms: number): Promise<void> => new Promise((ok) => setTimeout(ok, ms));

export function erstelleAnrufKarte(u: AnrufKarteUmgebung): AnrufKarte {
  const hole = u.holeKlingelnden ?? ((p: string) => klingelndenAbfragen(p));
  /** Präfixe, zu denen ENTSCHIEDEN ist — einmal je Anruf. */
  const entschieden = new Set<string>();
  /** Präfixe, zu denen gerade versucht wird — eine doppelte Zeile startet keinen zweiten Versuch. */
  const unterwegs = new Set<string>();
  let offen: Offen | null = null;

  const protokoll = (zeile: string): void => {
    try {
      u.spur(`[anruf-karte] ${zeile}`);
    } catch {
      /* Protokoll darf nie werfen */
    }
  };

  /** Was endgültig gegen eine Karte spricht — oder null. */
  const hindernis = (): string | null => {
    if (!u.darfAnrufen()) return "konto";
    if (!u.sichtbar()) return "hinten";
    if (u.imGespraech()) return "gespraech";
    return null;
  };

  /** Die Karte wegnehmen, Uhren aus — ohne Protokoll. */
  const schliesse = (): Offen | null => {
    const o = offen;
    if (!o) return null;
    offen = null;
    clearTimeout(o.ablauf);
    clearInterval(o.takt);
    try {
      o.ansicht.schliesse();
    } catch {
      /* die Ansicht ist schon weg */
    }
    return o;
  };

  const ende = (grund: AnrufKarteEnde): void => {
    const o = schliesse();
    if (o) protokoll(`weg ${o.praefix} grund=${grund}`);
  };

  const abheben = (): void => {
    // Erst weg, dann abheben: der Anruf startet ohne Karte darüber, und ein
    // Hinweis von behandleAnrufLink (Toast) ist sichtbar.
    const o = schliesse();
    if (!o) return;
    let ergebnis: string | void;
    try {
      ergebnis = u.abheben(o.callId);
    } catch {
      ergebnis = "fehler";
    }
    protokoll(`weg ${o.praefix} grund=abgehoben ergebnis=${ergebnis ?? "-"}`);
  };

  /** Klingelt es laut Server noch? Ein Fehler (auch das Zeitlimit) nimmt die Karte NICHT weg. */
  const pruefe = async (o: Offen): Promise<void> => {
    if (o.pruefungLaeuft) return;
    o.pruefungLaeuft = true;
    try {
      const a = await hole(o.praefix);
      if (offen === o && a?.callId !== o.callId) ende("vorbei");
    } catch {
      /* gerade nicht erreichbar — die Frist nimmt sie spätestens weg */
    } finally {
      o.pruefungLaeuft = false;
    }
  };

  const zeige = (praefix: string, a: KlingelnderAnruf): void => {
    ende("anderswo"); // es klingelt nie zweimal zugleich — eine alte Karte wäre überholt
    const ansicht = u.zeige({ abheben, ablehnen: () => ende("abgelehnt") });
    let schritte = 0;
    const o: Offen = {
      praefix,
      callId: a.callId,
      ansicht,
      pruefungLaeuft: false,
      ablauf: setTimeout(() => ende("abgelaufen"), a.restMs),
      takt: setInterval(() => {
        if (u.imGespraech()) {
          ende("gespraech");
          return;
        }
        schritte++;
        if ((schritte * ANRUF_KARTE_TAKT_MS) % ANRUF_KARTE_PRUEF_MS === 0) void pruefe(o);
      }, ANRUF_KARTE_TAKT_MS),
    };
    offen = o;
    protokoll(`zeigt ${praefix} rest=${Math.round(a.restMs / 1000)} s`);
    try {
      u.haptik();
      if (u.tonErlaubt()) u.ton();
    } catch {
      /* Hinweis ist Zugabe */
    }
  };

  /** Ein Versuch: fragen und, wenn alles passt, die Karte zeigen. */
  const versuche = async (praefix: string): Promise<Versuch> => {
    if (u.kontoBekannt && !u.kontoBekannt()) return { endgueltig: false, grund: "konto-unbekannt" };
    const vorher = hindernis();
    if (vorher) return { endgueltig: true, grund: vorher };
    let a: KlingelnderAnruf | null;
    try {
      a = await hole(praefix);
    } catch (e) {
      if (e instanceof KlingeltAbfrageFehler && !e.vorlaeufig) {
        return { endgueltig: true, grund: `abfrage-${e.grund}` };
      }
      return { endgueltig: false, grund: e instanceof KlingeltAbfrageFehler ? `abfrage-${e.grund}` : "abfrage-fehler" };
    }
    if (!a) return { endgueltig: true, grund: "klingelt-nicht" };
    // Während der Abfrage kann sich alles geändert haben.
    const nachher =
      hindernis() ??
      (u.schonBehandelt?.(a.callId) ? "behandelt" : null) ??
      (a.restMs < ANRUF_KARTE_MIN_REST_MS ? "zu-spaet" : null);
    if (nachher) return { endgueltig: true, grund: nachher };
    zeige(praefix, a);
    return { endgueltig: true, grund: null };
  };

  return {
    async spur(zeile) {
      const praefix = fokusAblehnungAus(zeile);
      if (!praefix || entschieden.has(praefix) || unterwegs.has(praefix)) return;
      unterwegs.add(praefix);
      const bis = Date.now() + ANRUF_KARTE_VERSUCH_MS;
      let gemeldet = false;
      try {
        for (;;) {
          const v = await versuche(praefix);
          if (v.endgueltig) {
            if (v.grund) protokoll(`keine ${praefix} grund=${v.grund}`);
            break;
          }
          if (!gemeldet) {
            protokoll(`wartet ${praefix} grund=${v.grund}`);
            gemeldet = true;
          }
          if (Date.now() + ANRUF_KARTE_NOCHMAL_MS >= bis) {
            protokoll(`keine ${praefix} grund=aufgegeben letzter=${v.grund}`);
            break;
          }
          await warte(ANRUF_KARTE_NOCHMAL_MS);
        }
      } finally {
        unterwegs.delete(praefix);
        entschieden.add(praefix);
      }
    },
    anderswo(callId) {
      if (callId && offen?.callId === callId) ende("anderswo");
    },
    offen: () => offen?.callId ?? null,
  };
}
